/**
 * Single properties panel: renders properties by component type.
 * Reads siteBuilderSelectedChild from Redux and updates the same child; dispatches UPDATE_COMPONENT.
 */
import {
	FormatBoldRounded,
	FormatItalicRounded,
	FormatListBulletedRounded,
	FormatListNumberedRounded,
	FormatStrikethroughRounded,
	FormatUnderlinedRounded,
	DeleteRounded,
	ContentCopyRounded,
	FormatAlignCenterRounded,
	FormatAlignJustifyRounded,
	FormatAlignLeftRounded,
	FormatAlignRightRounded,
	ImageRounded,
	OpacityRounded,
	LinkRounded,
	CloseRounded,
	ModeEditRounded,
	
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from 'notistack';
import { reduxHelper } from "../../../config/reduxHelper";
import { customComponentTypes } from "../../../customComponents/types";
import ThemeColorPicker from "../../themebuilder/themeColorPicker";
import ImageGallery from "../../themebuilder/ImageGallery";
import IconCollection from "../../themebuilder/componentEditor/IconCollection";
import { useColor, toColor } from "react-color-palette";
import { useEffect, useState } from "react";
import { validURL } from "../../../functions/urlValidation";

const VARIANTS = [
  { value: "body1", label: "Normal text" },
  { value: "h1", label: "Heading 1" },
  { value: "h2", label: "Heading 2" },
  { value: "h3", label: "Heading 3" },
  { value: "h4", label: "Heading 4" },
  { value: "h5", label: "Heading 5" },
  { value: "h6", label: "Heading 6" },
  { value: "subtitle1", label: "Subtitle 1" },
  { value: "subtitle2", label: "Subtitle 2" },
  { value: "caption", label: "Caption" },
];

const ALIGNMENTS = [
  { value: "left", Icon: FormatAlignLeftRounded },
  { value: "center", Icon: FormatAlignCenterRounded },
  { value: "right", Icon: FormatAlignRightRounded },
  { value: "justify", Icon: FormatAlignJustifyRounded },
];

const BUTTON_VARIANTS = [
  { value: "contained", label: "Filled" },
  { value: "outlined", label: "Outline" },
  { value: "text", label: "Text" },
];

const BORDER_STYLES = [
  { value: "solid", label: "Solid" },
  { value: "dashed", label: "Dashed" },
  { value: "dotted", label: "Dotted" },
];

const ICON_SIZES = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
];

const CARD_BORDER_RADIUS = [
	{ value: "Full", label: "Full" },
	{ value: "Medium", label: "Medium" },
	{ value: "Low", label: "Low" },
	{ value: "None", label: "None" },
];

const FONT_WEIGHTS = [
	{ value: 400, label: "Regular" },
	{ value: 500, label: "Medium" },
	{ value: 600, label: "SemiBold" },
	{ value: 700, label: "Bold" },
];

const FONT_SIZES = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64];

const IMAGE_OVERLAY_EFFECTS = [
	{ value: "clear", label: "Clear" },
	{ value: "faded", label: "Faded" },
	{ value: "frosted", label: "Frosted" },
];


const Sep = () => (
	<Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.25 }} />
);



export default function TextPropertiesPanel({ onChangeChild, childOverride, onDuplicateChild, onDeleteChild }) {
	const dispatch = useDispatch();
	const { siteBuilderSelectedChild, themebuilderSelectedItem } = useSelector(
		(s) => s.reducer
	);
	const { enqueueSnackbar } = useSnackbar();
	const child = childOverride || siteBuilderSelectedChild;

	/* ── type flags ──────────────────────────────────────────────────────── */
	const isText = child?.type === customComponentTypes.typography;
	const isButton = child?.type === customComponentTypes.button || child?.type === 'flexible-button';
	const isImage = child?.type === customComponentTypes.image;
	const isDivider = child?.type === customComponentTypes.divider;
	const isIcon = child?.type === customComponentTypes.icon || child?.type === 'flexible-icon';
	const isLogo = child?.type === customComponentTypes.logo;
	const isSpacer = child?.type === customComponentTypes.spacer || child?.type === 'flexible-spacer';
	const isFeatureCard = child?.type === customComponentTypes.feature1 || child?.type === 'flexible-featureCard';
	const isImageCard = child?.type === customComponentTypes.image1;
	const isIconCard = child?.type === customComponentTypes.icon1;
	const isCard = child?.type === customComponentTypes.card;
	const isGenericCard = child?.type === customComponentTypes.genericCard || child?.type === 'flexible-genericCard';
	const isContent = child?.type === customComponentTypes.content || child?.type === customComponentTypes.content2 || child?.type === customComponentTypes.content3 || child?.type === 'flexible-content';
	const isContentWithImage = (child?.type === 'flexible-content' && (child?.variant === '2' || child?.variant === '3')) || child?.type === customComponentTypes.content2 || child?.type === customComponentTypes.content3;
	const isTypo1 = child?.type === customComponentTypes.typography1;
	const isForm = child?.type === customComponentTypes.form || child?.type === 'flexible-form';
	const isDropdown = child?.type === customComponentTypes.popover || child?.type === 'flexible-dropdown';
	const isPopover = child?.type === customComponentTypes.popover;
	const isTable = child?.type === 'table';

	const [showLinkInput, setShowLinkInput] = useState(false);
	const [localUrl, setLocalUrl] = useState("");
	const [errorUrl, setErrorUrl] = useState(false);

	const urlVal = typeof child?.url === "string"
		? child.url
		: (child?.url?.path ?? "");

	// Update localUrl when showLinkInput is toggled
	useEffect(() => {
		if (showLinkInput) {
			setLocalUrl(urlVal);
		}
	}, [showLinkInput, urlVal]);

	const hasPanel =
		isText || isButton || isImage || isDivider || isIcon || isLogo ||
		isSpacer || isFeatureCard || isImageCard || isIconCard || isCard ||
		isGenericCard || isContent || isContentWithImage || isTypo1 || isPopover || isTable;

	/* ── derived ─────────────────────────────────────────────────────────── */
	const style = child?.props?.style || {};
	const sx = child?.props?.sx || {};
	const btnRadius = (() => {
		const raw = sx?.borderRadius;
		if (raw === undefined || raw === null || raw === "") return 8;
		if (typeof raw === "number") return raw;
		const parsed = Number.parseFloat(raw);
		return Number.isFinite(parsed) ? parsed : 8;
	})();

	/* ── colour picker hooks ─────────────────────────────────────────────── */
	const [textColor, setTextColor] = useColor("hex", "#000000");
	const [accentColor, setAccentColor] = useColor("hex", "#000000");
	const [fillColor, setFillColor] = useColor("hex", "#e0e0e0");

	// Local states for sliders for smooth interaction
	const [localPadding, setLocalPadding] = useState(1.5);
	const [localRadius, setLocalRadius] = useState(0);
	const [localThickness, setLocalThickness] = useState(1);
	const [localIntensity, setLocalIntensity] = useState(20);
	const [localSpacerSize, setLocalSpacerSize] = useState(0);

	useEffect(() => {
		if (isTable) setLocalPadding(child?.props?.style?.padding !== undefined ? child.props.style.padding : 1.5);
		if (isButton || isGenericCard || isFeatureCard || isImageCard) {
			const r = isButton ? parseInt(btnRadius) : (parseInt(child?.props?.borderRadius) || 0);
			setLocalRadius(r || 0);
		}
		if (isDivider) setLocalThickness(Number(sx?.borderWidth ?? 1));
		if (isImage && child?.props?.overlayEffect !== "clear") setLocalIntensity(Number(child?.props?.overlayIntensity ?? 20));
		if (isSpacer) {
			const size = child?.variant === 'vertical' ? (Number(child?.props?.sx?.height) || 0) : (parseInt(child?.props?.sx?.width) || 0);
			setLocalSpacerSize(size);
		}
	}, [child?.id, child?.props, isTable, isButton, isGenericCard, isFeatureCard, isImageCard, isDivider, isImage, isSpacer, btnRadius, sx?.borderWidth]);

	useEffect(() => {
		if (!child) return;
		try {
			const tc = child?.props?.style?.color;
			if (tc) setTextColor(toColor("hex", tc));

			if (isDivider && child?.props?.sx?.borderColor)
				setAccentColor(toColor("hex", child.props.sx.borderColor));
			else if (isIcon && child?.props?.style?.color)
				setAccentColor(toColor("hex", child.props.style.color));
			else if ((isFeatureCard || isIconCard || isContent) && child?.props?.style4?.color)
				setAccentColor(toColor("hex", child.props.style4.color));

			if (isButton) {
				const bHex =
					child?.props?.variant === "outlined"
						? child?.props?.sx?.borderColor
						: child?.props?.sx?.backgroundColor;
				if (bHex) setFillColor(toColor("hex", bHex));
			}
		} catch (_) { }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [child?.type, child?.id]);

	/* ── early exit ──────────────────────────────────────────────────────── */
	if (!child || !hasPanel) return null;

	/* ── shared dispatch helpers ─────────────────────────────────────────── */
	const update = () => {
		if (typeof onChangeChild === "function" && child) {
			const nextChild = {
				...child,
				props: child.props ? JSON.parse(JSON.stringify(child.props)) : child.props,
				content: child.content ? JSON.parse(JSON.stringify(child.content)) : child.content,
			};
			onChangeChild(nextChild);
			// In nested FlexibleEditor flow, direct sync is source of truth.
			// Update Redux selection to avoid closure due to stale reference check.
			if (childOverride) {
				dispatch({ type: reduxHelper.UPDATE_SITE_BUILDER_SELECTED_CHILD, payload: nextChild });
				return;
			}
		}
		dispatch({ type: reduxHelper.UPDATE_COMPONENT });
	};

	const setStyle = (key, val) => {
		const props = { ...(child.props || {}) };
		const style = { ...(props.style || {}) };
		if (val === undefined) {
			delete style[key];
		} else {
			style[key] = val;
		}
		props.style = style;
		child.props = props;
		update();
	};

	const setSx = (key, val) => {
		const props = { ...(child.props || {}) };
		const sx = { ...(props.sx || {}) };
		if (val === undefined) {
			delete sx[key];
		} else {
			sx[key] = val;
		}
		props.sx = sx;
		child.props = props;
		update();
	};

	const setProp = (key, val) => {
		const props = { ...(child.props || {}) };
		if (val === undefined) {
			delete props[key];
		} else {
			props[key] = val;
		}
		child.props = props;
		update();
	};

	const setNested = (styleKey, key, val) => {
		const props = { ...(child.props || {}) };
		const nested = { ...(props[styleKey] || {}) };
		nested[key] = val;
		props[styleKey] = nested;
		child.props = props;
		update();
	};

	const toggleStyle = (key, toggleVal) => {
		const props = { ...(child.props || {}) };
		const style = { ...(props.style || {}) };
		style[key] = style[key] === toggleVal ? undefined : toggleVal;
		props.style = style;
		child.props = props;
		update();
	};

	const setChildUrl = (val) => {
		// If child is frozen, we might need a more complex solution, 
		// but for now let's hope child itself is mutable.
		child.url = val;
		update();
	};

	const LinkOffRounded = (props) => (
		<Box component="span" {...props} sx={{ ...props.sx, display: "flex", alignItems: "center" }}>
			<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
				<path d="M17 7h-4v2h4c1.65 0 3 1.35 3 3 0 .36-.06.71-.17 1.05l1.46 1.46c.45-.78.71-1.68.71-2.51 0-3.31-2.69-6-6-6zm-5 4.17V11H7c-1.65 0-3-1.35-3-3s1.35-3 3-3h4V3H7C4.24 3 2 5.24 2 8s2.24 5 5 5h3.17l2.83 2.83V16h4c.71 0 1.37-.25 1.9-.66L20.59 18l1.41-1.41-16-16-1.41 1.41 3.55 3.55c-.21.46-.35.97-.43 1.5H3.17L2 8.17 3.17 7h3.17l-.34 1.5H7.17L6 7.17l1.17 1H10V7h2V8.17l2 2V12h2.83l2 2H16c-3 0-5-2-5-5V8.17z" />
			</svg>
		</Box>
	);

	const CheckRounded = (props) => (
		<Box component="span" {...props} sx={{ ...props.sx, display: "flex", alignItems: "center" }}>
			<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
				<path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
			</svg>
		</Box>
	);

	const handleLinkEnter = (e) => {
		if (e.key === "Enter") {
			if (syncUrl(e.target.value)) {
				setShowLinkInput(false);
			}
		}
	};

	const syncUrl = (val) => {
		if (!val) {
			setChildUrl("");
			setErrorUrl(false);
			return true;
		}
		let finalVal = val.trim();
		if (finalVal && !finalVal.startsWith("http") && !finalVal.startsWith("/") && !finalVal.startsWith("#")) {
			finalVal = "http://" + finalVal;
		}
		if (validURL(finalVal)) {
			setChildUrl(finalVal);
			setErrorUrl(false);
			return true;
		} else {
			setErrorUrl(true);
			enqueueSnackbar("Invalid URL. Please enter a proper link.", { variant: 'error' });
			return false;
		}
	};

	const handleLinkBlur = (e) => {
		syncUrl(e.target.value);
	};

	const handleDuplicate = () => {
		if (typeof onDuplicateChild === "function") {
			onDuplicateChild(child);
			return;
		}
		const children = themebuilderSelectedItem?.children;
		if (!Array.isArray(children)) return;
		const clone = { ...child, id: Math.round(Math.random() * 100000) };
		const idx = children.indexOf(child);
		children.splice(idx + 1, 0, clone);
		update();
	};

	const handleDelete = () => {
		if (typeof onDeleteChild === "function") {
			onDeleteChild(child);
			return;
		}
		const children = themebuilderSelectedItem?.children;
		if (!Array.isArray(children)) return;
		const idx = children.indexOf(child);
		if (idx > -1) { children.splice(idx, 1); update(); }
		dispatch({ type: reduxHelper.UPDATE_SITE_BUILDER_SELECTED_CHILD, payload: null });
	};

	const handleClose = () => {
		dispatch({ type: reduxHelper.UPDATE_SITE_BUILDER_SELECTED_CHILD, payload: null });
	};

	/* ── derived ─────────────────────────────────────────────────────────── */
	const variant = child?.props?.variant || "body1";
	const textAlign = style?.textAlign || "left";
	const btnVariant = child?.props?.variant || "contained";
	const btnAlign = child?.props?.align || "left";

	/* ── icon button factory ─────────────────────────────────────────────── */
	const ib = (title, Icon, onClick, active = false) => (
		<Tooltip title={title} key={title} placement="top">
			<IconButton
				size="small"
				onMouseDown={(e) => {
					// Prevent editor focus changes from swallowing toolbar actions.
					e.preventDefault();
					e.stopPropagation();
				}}
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					onClick?.(e);
				}}
				sx={{
					color: active ? "primary.main" : "text.primary",
					bgcolor: active ? "primary.lighter" : "transparent",
					borderRadius: 1,
					p: 0.4,
					"&:hover": { bgcolor: active ? "primary.lighter" : "action.hover" },
				}}
			>
				<Icon sx={{ fontSize: 17 }} />
			</IconButton>
		</Tooltip>
	);


	const renderLinkInput = () => {
		if (showLinkInput) {
			return (
				<Stack direction="row" alignItems="center" gap={0.5}>
					<TextField
						error={errorUrl}
						size="small"
						autoFocus
						placeholder="Paste a link"
						value={localUrl}
						onChange={(e) => {
							setLocalUrl(e.target.value);
							if (errorUrl) setErrorUrl(false);
						}}
						onKeyDown={handleLinkEnter}
						onBlur={handleLinkBlur}
						variant="outlined"
						sx={{
							width: 180,
							"& .MuiOutlinedInput-root": {
								height: 28,
								fontSize: 12,
								bgcolor: "background.neutral",
							},
						}}
					/>
					{ib("Done", CheckRounded, () => {
						if (syncUrl(localUrl)) {
							setShowLinkInput(false);
						}
					})}
					{ib("Remove link", LinkOffRounded, () => {
						setChildUrl("");
						setShowLinkInput(false);
					})}
				</Stack>
			);
		}
		return ib("Add link", LinkRounded, () => setShowLinkInput(true), !!urlVal);
	};

	/* ── button colours ──────────────────────────────────────────────────── */
	const applyBtnColor = (c) => {
		setFillColor(c);
	};

	const commitBtnColor = (c) => {
		if (btnVariant === "outlined") {
			setSx("borderColor", c.hex);
			setSx("border", `2px solid ${c.hex}`);
		} else {
			setSx("backgroundColor", c.hex);
		}
	};

	const applyBtnVariant = (val) => {
		setProp("variant", val);
		if (val === "outlined") {
			setSx("backgroundColor", undefined);
			setSx("borderColor", fillColor.hex);
			setSx("border", `2px solid ${fillColor.hex}`);
		} else if (val === "text") {
			setSx("border", undefined);
			setSx("borderColor", undefined);
			setSx("backgroundColor", undefined);
		} else {
			setSx("border", undefined);
			setSx("borderColor", undefined);
			setSx("backgroundColor", fillColor.hex);
		}
	};

	const applyBtnAlign = (val) => {
		const map = {
			left: "flex-start",
			center: "center",
			right: "flex-end",
		};
		setProp("align", val);
		setSx("alignSelf", map[val] || "flex-start");
	};

	/* ─────────────────────────────────────────────────────────────────────── */
	/*  PANEL  (absolute, floats at top:-52)                                  */
	/* ─────────────────────────────────────────────────────────────────────── */
	return (
		<Box
			sx={{
				bgcolor: "background.paper",
				border: "1px solid",
				borderColor: "divider",
				borderRadius: 1.5,
				boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
				maxWidth: "calc(100vw - 32px)",
				overflowX: "auto",
				width: "100%",
				pb: "6px", // Space for scrollbar
				// Custom scrollbar
				"&::-webkit-scrollbar": {
					height: "6px",
					display: "block",
				},
				"&::-webkit-scrollbar-thumb": {
					bgcolor: "primary.main",
					borderRadius: "4px",
				},
				"&::-webkit-scrollbar-track": {
					bgcolor: "action.hover",
					borderRadius: "4px",
				},
			}}
		>
			<Stack
				direction="row"
				alignItems="center"
				gap={0.5}
				sx={{
					px: 1,
					py: 0.6,
					minHeight: 40,
					flexWrap: "nowrap",
					"& > *": {
						flexShrink: 0,
					},
				}}
			>
				{/* Close / deselect */}
				{ib("Deselect", CloseRounded, handleClose)}
				<Sep />


				{isText && (
				<>
					{/* Variant */}
					<Select
						size="small"
						value={variant}
						onChange={(e) => setProp("variant", e.target.value)}
						variant="standard" disableUnderline
						sx={{ minWidth: 76, fontSize: 13 }}
					>
						{VARIANTS.map((v) => (
							<MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
						))}
					</Select>
					<Sep />

					{/* Font size */}
					<Select
						size="small"
						value={style.fontSize ?? ""}
						onChange={(e) => setStyle("fontSize", e.target.value)}
						variant="standard" disableUnderline displayEmpty
						sx={{ minWidth: 50, fontSize: 13 }}
					>
						<MenuItem value=""><em>Size</em></MenuItem>
						{FONT_SIZES.map((s) => (
							<MenuItem key={s} value={s}>{s}</MenuItem>
						))}
					</Select>
					<Sep />

					{/* Text colour */}
					<Tooltip title="Text colour" placement="top">
						<Box sx={{ display: "flex", alignItems: "center" }}>
							<ThemeColorPicker
								color={textColor}
								setColor={(c) => { setTextColor(c); }}
								onCommit={(c) => { setStyle("color", c.hex); }}
							/>
						</Box>
					</Tooltip>
					<Sep />

					{/* Bold / Italic / Underline / Strikethrough */}
					{ib("Bold", FormatBoldRounded, () => toggleStyle("fontWeight", 700), style.fontWeight === 700)}
					{ib("Italic", FormatItalicRounded, () => toggleStyle("fontStyle", "italic"), style.fontStyle === "italic")}
					{ib("Underline", FormatUnderlinedRounded, () => toggleStyle("textDecoration", "underline"), style.textDecoration === "underline")}
					{ib("Strikethrough", FormatStrikethroughRounded, () => toggleStyle("textDecoration", "line-through"), style.textDecoration === "line-through")}
					<Sep />

					{/* Lists */}
					{ib("Bulleted list", FormatListBulletedRounded, () => toggleStyle("listType", "bullet"), style.listType === "bullet")}
					{ib("Numbered list", FormatListNumberedRounded, () => toggleStyle("listType", "number"), style.listType === "number")}
					<Sep />

					{ALIGNMENTS.map(({ value, Icon }) =>
						ib(value, Icon, () => setStyle("textAlign", value), textAlign === value)
					)}
					<Sep />

					{ib("Duplicate", ContentCopyRounded, handleDuplicate)}
					{ib("Delete", DeleteRounded, handleDelete)}
				</>
			)}

			{/* ━━━━━━━━━━━━━━━━━━━ TABLE ━━━━━━━━━━━━━━━━━━━━━━━━━ */}
			{isTable && (
				<>
					<Tooltip title="Table alignment" placement="top">
						<Stack direction="row" spacing={0.25}>
							{ALIGNMENTS.filter(a => a.value !== 'justify').map(({ value, Icon }) =>
								ib(value, Icon, () => setNested('style', 'tableAlign', value), (child.props?.style?.tableAlign || 'left') === value)
							)}
						</Stack>
					</Tooltip>
					<Sep />
					<Tooltip title="Header background" placement="top">
						<Box>
							<ThemeColorPicker
								color={toColor('hex', child.props?.style?.headerBg || '#f5f5f5')}
								setColor={(c) => {}}
								onCommit={(c) => setNested('style', 'headerBg', c.hex)}
							/>
						</Box>
					</Tooltip>
					<Sep />
					<Slider
						size="small"
						min={0.5}
						max={5}
						step={0.5}
						value={localPadding}
						onChange={(_, v) => setLocalPadding(v)}
						onChangeCommitted={(_, v) => setNested('style', 'padding', v)}
						valueLabelDisplay="auto"
						sx={{ width: 60, mx: 1 }}
					/>
					<Sep />
					{ib("Duplicate", ContentCopyRounded, handleDuplicate)}
					{ib("Delete", DeleteRounded, handleDelete)}
				</>
			)}

			{/* ━━━━━━━━━━━━━━━━━━━ BUTTON ━━━━━━━━━━━━━━━━━━━━━━━━ */}
			{isButton && (
				<>
					<Select
						size="small"
						value={btnVariant}
						onChange={(e) => applyBtnVariant(e.target.value)}
						variant="standard" disableUnderline
						sx={{ minWidth: 72, fontSize: 13 }}
					>
						{BUTTON_VARIANTS.map((v) => (
							<MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
						))}
					</Select>
					<Sep />
					<Tooltip title={btnVariant === "outlined" ? "Border colour" : "Fill colour"} placement="top">
						<Box><ThemeColorPicker color={fillColor} setColor={applyBtnColor} onCommit={commitBtnColor} /></Box>
					</Tooltip>
					<Tooltip title="Text colour" placement="top">
						<Box>
							<ThemeColorPicker
								color={textColor}
								setColor={(c) => { setTextColor(c); }}
								onCommit={(c) => { setStyle("color", c.hex); }}
							/>
						</Box>
					</Tooltip>
					<Sep />
					{ALIGNMENTS.filter((a) => a.value !== "justify").map(({ value, Icon }) =>
						ib(value, Icon, () => applyBtnAlign(value), btnAlign === value)
					)}
					<Sep />
					<Slider
						size="small"
						min={0}
						max={50}
						step={1}
						value={localRadius}
						onChange={(_, v) => setLocalRadius(v)}
						onChangeCommitted={(_, v) => setSx("borderRadius", `${v}px`)}
						valueLabelDisplay="auto"
						sx={{ width: 70 }}
					/>
					{renderLinkInput()}
					<Sep />
					{ib("Duplicate", ContentCopyRounded, handleDuplicate)}
					{ib("Delete", DeleteRounded, handleDelete)}
				</>
			)}

			{/* ━━━━━━━━━━━━━━━━━━━ IMAGE ━━━━━━━━━━━━━━━━━━━━━━━━━ */}
			{isImage && (
				<>
					{ALIGNMENTS.filter((a) => a.value !== "justify").map(({ value, Icon }) =>
						ib(`Align ${value}`, Icon, () => {
							const map = {
								left: { marginLeft: 0, marginRight: "auto" },
								center: { marginLeft: "auto", marginRight: "auto" },
								right: { marginLeft: "auto", marginRight: 0 },
							};
							if (!child.props) child.props = {};
							if (!child.props.sx) child.props.sx = {};
							child.props.align = value;
							Object.assign(child.props.sx, map[value] || {});
							update();
						}, (child?.props?.align || "left") === value)
					)}
					<Sep />
					<Select
						size="small"
						value={child?.props?.overlayEffect ?? "clear"}
						onChange={(e) => { if (!child.props) child.props = {}; child.props.overlayEffect = e.target.value; update(); }}
						variant="standard" disableUnderline
						sx={{ minWidth: 68, fontSize: 13 }}
					>
						{IMAGE_OVERLAY_EFFECTS.map((o) => (
							<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
						))}
					</Select>
					{(child?.props?.overlayEffect === "faded" || child?.props?.overlayEffect === "frosted") && (
						<>
							<Tooltip title="Overlay colour" placement="top">
								<Box>
									<ThemeColorPicker
										color={toColor("hex", child?.props?.overlayColor || "#000000")}
										setColor={(c) => {}}
										onCommit={(c) => { if (!child.props) child.props = {}; child.props.overlayColor = c.hex; update(); }}
									/>
								</Box>
							</Tooltip>
							<OpacityRounded sx={{ fontSize: 15, color: "text.secondary" }} />
							<Slider
								size="small"
								value={localIntensity}
								onChange={(_, v) => setLocalIntensity(v)}
								onChangeCommitted={(_, v) => { if (!child.props) child.props = {}; child.props.overlayIntensity = v; update(); }}
								min={0} max={100}
								sx={{ width: 70 }}
							/>
						</>
					)}
					<Sep />
					{ib("Duplicate", ContentCopyRounded, handleDuplicate)}
					{ib("Delete", DeleteRounded, handleDelete)}
				</>
			)}

			{/* ━━━━━━━━━━━━━━━━━━━ DIVIDER ━━━━━━━━━━━━━━━━━━━━━━━ */}
			{isDivider && (
				<>
					<Tooltip title="Colour" placement="top">
						<Box>
							<ThemeColorPicker
								color={accentColor}
								setColor={(c) => { setAccentColor(c); }}
								onCommit={(c) => { setSx("borderColor", c.hex); }}
							/>
						</Box>
					</Tooltip>
					<Sep />
					{BORDER_STYLES.map(({ value, label }) => (
						<Chip
							key={value}
							size="small"
							label={label}
							onClick={() => setSx("borderStyle", value)}
							color={(sx.borderStyle || "solid") === value ? "primary" : "default"}
							sx={{ height: 22, fontSize: 11, borderRadius: 1 }}
						/>
					))}
					<Sep />
					<Slider
						size="small"
						min={1}
						max={10}
						step={1}
						value={localThickness}
						onChange={(_, v) => setLocalThickness(v)}
						onChangeCommitted={(_, v) => setSx("borderWidth", v)}
						valueLabelDisplay="auto"
						sx={{ width: 70, mx: 1 }}
					/>
					<Sep />
					{ib("Duplicate", ContentCopyRounded, handleDuplicate)}
					{ib("Delete", DeleteRounded, handleDelete)}
				</>
			)}

			{/* ━━━━━━━━━━━━━━━━━━━ ICON ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
			{isIcon && (
				<>
					{/* IconCollection wraps children in its own <IconButton> */}
					<IconCollection
						onPickIcon={(icon) => { child.children = icon; update(); }}
						selectedIcon={child?.children || "vector-selection"}
					>
						<Tooltip title="Change icon" placement="top">
							<Box
								component="span"
								className={`mdi mdi-${child?.children || "vector-selection"}`}
								sx={{ fontSize: 20, lineHeight: 1.2 }}
							/>
						</Tooltip>
					</IconCollection>
					<Tooltip title="Icon colour" placement="top">
						<Box>
							<ThemeColorPicker
								color={accentColor}
								setColor={(c) => { setAccentColor(c); }}
								onCommit={(c) => { setStyle("color", c.hex); }}
							/>
						</Box>
					</Tooltip>
					<Tooltip title="Icon background colour" placement="top">
						<Box>
							<ThemeColorPicker
								color={toColor("hex", child?.props?.style3?.color || "#5b6aac")}
								setColor={(c) => {}}
								onCommit={(c) => setNested("style3", "color", c.hex)}
							/>
						</Box>
					</Tooltip>
					<Sep />
					<Select
						size="small"
						value={child?.props?.fontSize || "medium"}
						onChange={(e) => setProp("fontSize", e.target.value)}
						variant="standard" disableUnderline
						sx={{ minWidth: 48, fontSize: 13 }}
					>
						{ICON_SIZES.map((s) => (
							<MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
						))}
					</Select>
					{renderLinkInput()}
					<Sep />
					{ib("Duplicate", ContentCopyRounded, handleDuplicate)}
					{ib("Delete", DeleteRounded, handleDelete)}
				</>
			)}

			{isLogo && (
				<>
					{ib("Duplicate", ContentCopyRounded, handleDuplicate)}
					{ib("Delete", DeleteRounded, handleDelete)}
				</>
			)}

			{isSpacer && (
				<Box sx={{ width: '100%', py: 1 }}>
					<Stack gap={2}>
						{/* Mode Toggle */}

						{/* Mode Toggle */}
						<Stack direction="row" gap={1}>
							<Chip
								label="Horizontal"
								size="small"
								onClick={() => {
									if (!child.props) child.props = {};
									if (!child.props.sx) child.props.sx = {};
									child.variant = 'horizontal';
									// Initialize width if not present
									if (!child.props.sx.width) {
										child.props.sx.width = '20%';
										child.props.width = '20%';
									}
									update();
								}}
								color={child?.variant === 'horizontal' ? 'info' : 'default'}
								sx={{ borderRadius: 1 }}
							/>
							<Chip
								label="Vertical"
								size="small"
								onClick={() => {
									if (!child.props) child.props = {};
									if (!child.props.sx) child.props.sx = {};
									child.variant = 'vertical';
									// Initialize height if not present
									if (!child.props.sx.height) {
										child.props.sx.height = 40;
										child.props.height = 40;
									}
									update();
								}}
								color={child?.variant === 'vertical' ? 'info' : 'default'}
								sx={{ borderRadius: 1 }}
							/>
						</Stack>

						{/* Sizing Slider */}
						<Stack direction="row" alignItems="center" gap={2} sx={{ bgcolor: 'background.paper', px: 2, py: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
							<Slider
								value={localSpacerSize}
								onChange={(e, v) => setLocalSpacerSize(v)}
								onChangeCommitted={(e, v) => {
									const props = { ...(child.props || {}) };
									if (!props.sx) props.sx = {};
									if (child.variant === 'vertical') {
										props.sx.height = v;
										props.height = v;
									} else {
										props.sx.width = `${v}%`;
										props.width = `${v}%`;
									}
									child.props = props;
									update();
								}}
								min={0}
								max={child?.variant === 'vertical' ? 200 : 100}
								step={child?.variant === 'vertical' ? 10 : 5}
								size="small"
								color="info"
							/>
							<Typography variant="caption" sx={{ fontWeight: 600, minWidth: 24 }}>
								{child?.variant === 'vertical' ? (child?.props?.sx?.height || 0) : (parseInt(child?.props?.sx?.width) || 0)}
								{child?.variant === 'horizontal' ? '%' : ''}
							</Typography>
						</Stack>

						<Stack direction="row" justifyContent="flex-end" gap={1}>
							{ib("Duplicate", ContentCopyRounded, handleDuplicate)}
							{ib("Delete", DeleteRounded, handleDelete)}
						</Stack>
					</Stack>
				</Box>
			)}

			{isFeatureCard && (
				<>
					<IconCollection
						onPickIcon={(icon) => { if (!child.content) child.content = {}; child.content.icon = icon; update(); }}
						selectedIcon={child?.content?.icon || "star"}
					>
						<Tooltip title="Icon" placement="top">
							<Box
								component="span"
								className={`mdi mdi-${child?.content?.icon || "star"}`}
								sx={{ fontSize: 20, lineHeight: 1.2 }}
							/>
						</Tooltip>
					</IconCollection>
					<Sep />
					<Tooltip title="Card background" placement="top">
						<Box>
							<ThemeColorPicker
								color={toColor("hex", child?.props?.style2?.color || "#f5f5f5")}
								setColor={(c) => {}}
								onCommit={(c) => setNested("style2", "color", c.hex)}
							/>
						</Box>
					</Tooltip>
					<Tooltip title="Icon circle" placement="top">
						<Box>
							<ThemeColorPicker
								color={toColor("hex", child?.props?.style3?.color || "#e0e0e0")}
								setColor={(c) => {}}
								onCommit={(c) => setNested("style3", "color", c.hex)}
							/>
						</Box>
					</Tooltip>
					<Tooltip title="Icon colour" placement="top">
						<Box>
							<ThemeColorPicker
								color={accentColor}
								setColor={(c) => { setAccentColor(c); }}
								onCommit={(c) => { setNested("style4", "color", c.hex); }}
							/>
						</Box>
					</Tooltip>
					<Tooltip title="Text colour" placement="top">
						<Box>
							<ThemeColorPicker
								color={toColor("hex", child?.props?.style5?.textcolor || "#000000")}
								setColor={(c) => {}}
								onCommit={(c) => setNested("style5", "textcolor", c.hex)}
							/>
						</Box>
					</Tooltip>
					<Sep />
					<Select
						size="small"
						value={child?.props?.borderRadius || "None"}
						onChange={(e) => setProp("borderRadius", e.target.value)}
						variant="standard" disableUnderline
						sx={{ minWidth: 58, fontSize: 13 }}
					>
						{CARD_BORDER_RADIUS.map((r) => (
							<MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
						))}
					</Select>
					<Select
						size="small"
						value={child?.props?.fontSize || "large"}
						onChange={(e) => setProp("fontSize", e.target.value)}
						variant="standard" disableUnderline
						sx={{ minWidth: 42, fontSize: 13 }}
					>
						{ICON_SIZES.map((s) => (
							<MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
						))}
					</Select>
					{renderLinkInput()}
					<Sep />
					{ib("Duplicate", ContentCopyRounded, handleDuplicate)}
					{ib("Delete", DeleteRounded, handleDelete)}
				</>
			)}

			{isImageCard && (
				<>
					{/* ImageGallery wraps children in its own <IconButton> */}
					<ImageGallery
						onAddImage={(img) => setNested("content", "src", img)}
					>
						<Tooltip title="Change image" placement="top">
							<ImageRounded sx={{ fontSize: 20 }} />
						</Tooltip>
					</ImageGallery>
					<Sep />
					<Select
						size="small"
						value={child?.props?.borderRadius || "Medium"}
						onChange={(e) => setProp("borderRadius", e.target.value)}
						variant="standard" disableUnderline
						sx={{ minWidth: 58, fontSize: 13 }}
					>
						{CARD_BORDER_RADIUS.map((r) => (
							<MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
						))}
					</Select>
					{renderLinkInput()}
					<Sep />
					{ib("Duplicate", ContentCopyRounded, handleDuplicate)}
					{ib("Delete", DeleteRounded, handleDelete)}
				</>
			)}

			{isIconCard && (
				<>
					<IconCollection
						onPickIcon={(icon) => { if (!child.content) child.content = {}; child.content.icon = icon; update(); }}
						selectedIcon={child?.content?.icon || "star"}
					>
						<Tooltip title="Icon" placement="top">
							<Box
								component="span"
								className={`mdi mdi-${child?.content?.icon || "star"}`}
								sx={{ fontSize: 20, lineHeight: 1.2 }}
							/>
						</Tooltip>
					</IconCollection>
					<Tooltip title="Icon colour" placement="top">
						<Box>
							<ThemeColorPicker
								color={accentColor}
								setColor={(c) => { setAccentColor(c); }}
								onCommit={(c) => { setNested("style4", "color", c.hex); }}
							/>
						</Box>
					</Tooltip>
					<Sep />
					<Select
						size="small"
						value={child?.props?.fontSize || "medium"}
						onChange={(e) => setProp("fontSize", e.target.value)}
						variant="standard" disableUnderline
						sx={{ minWidth: 44, fontSize: 13 }}
					>
						{ICON_SIZES.map((s) => (
							<MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
						))}
					</Select>
					<Sep />
					{renderLinkInput()}
					<Sep />
					{ib("Duplicate", ContentCopyRounded, handleDuplicate)}
					{ib("Delete", DeleteRounded, handleDelete)}
				</>
			)}

			{(isCard || isGenericCard) && (
				<>
					{renderLinkInput()}
					<Sep />
					<Select
						size="small"
						value={child?.props?.alignItems || "center"}
						onChange={(e) => {
							const val = e.target.value;
							const textAlign = val === "flex-start" ? "left" : val === "flex-end" ? "right" : "center";
							const props = { ...(child.props || {}) };
							props.alignItems = val;
							props.textAlign = textAlign;
							child.props = props;
							update();
						}}
						variant="standard"
						disableUnderline
						sx={{ minWidth: 60, fontSize: 12 }}
					>
						<MenuItem value="center">Center</MenuItem>
						<MenuItem value="flex-start">Left</MenuItem>
						<MenuItem value="flex-end">Right</MenuItem>
					</Select>
					<Sep />

					{/* Title Style */}
					<Tooltip title="Title colour" placement="top">
						<Box sx={{ display: "flex", alignItems: "center" }}>
							<ThemeColorPicker
								color={toColor("hex", child?.props?.style?.color || "#000000")}
								setColor={(c) => {}}
								onCommit={(c) => setNested("style", "color", c.hex)}
							/>
						</Box>
					</Tooltip>
					<IconButton
						size="small"
						onClick={() => setNested("style", "fontWeight", child?.props?.style?.fontWeight === 700 ? 500 : 700)}
						color={child?.props?.style?.fontWeight === 700 ? "primary" : "default"}
					>
						<FormatBoldRounded fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						onClick={() => setNested("style", "fontStyle", child?.props?.style?.fontStyle === "italic" ? "normal" : "italic")}
						color={child?.props?.style?.fontStyle === "italic" ? "primary" : "default"}
					>
						<FormatItalicRounded fontSize="small" />
					</IconButton>
					<Select
						size="small"
						value={child?.props?.style?.fontSize || 16}
						onChange={(e) => setNested("style", "fontSize", e.target.value)}
						variant="standard"
						disableUnderline
						sx={{ minWidth: 40, fontSize: 13 }}
					>
						{FONT_SIZES.map((s) => (
							<MenuItem key={s} value={s}>{s}</MenuItem>
						))}
					</Select>
					<Sep />

					{/* Description Style */}
					<Tooltip title="Desc colour" placement="top">
						<Box sx={{ display: "flex", alignItems: "center" }}>
							<ThemeColorPicker
								color={toColor("hex", child?.props?.style2?.color || "#555555")}
								setColor={(c) => {}}
								onCommit={(c) => setNested("style2", "color", c.hex)}
							/>
						</Box>
					</Tooltip>
					<IconButton
						size="small"
						onClick={() => setNested("style2", "fontWeight", child?.props?.style2?.fontWeight === 700 ? 400 : 700)}
						color={child?.props?.style2?.fontWeight === 700 ? "primary" : "default"}
					>
						<FormatBoldRounded fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						onClick={() => setNested("style2", "fontStyle", child?.props?.style2?.fontStyle === "italic" ? "normal" : "italic")}
						color={child?.props?.style2?.fontStyle === "italic" ? "primary" : "default"}
					>
						<FormatItalicRounded fontSize="small" />
					</IconButton>
					<Select
						size="small"
						value={child?.props?.style2?.fontSize || 14}
						onChange={(e) => setNested("style2", "fontSize", e.target.value)}
						variant="standard"
						disableUnderline
						sx={{ minWidth: 36, fontSize: 12 }}
					>
						{FONT_SIZES.map((s) => (
							<MenuItem key={s} value={s}>{s}</MenuItem>
						))}
					</Select>
					<Sep />

					{/* Card Background & Border */}
					<Tooltip title="Card Background" placement="top">
						<Box sx={{ display: "flex", alignItems: "center" }}>
							<ThemeColorPicker
								color={toColor("hex", child?.props?.style?.backgroundColor || "#ffffff")}
								setColor={(c) => {}}
								onCommit={(c) => setNested("style", "backgroundColor", c.hex)}
							/>
						</Box>
					</Tooltip>
					<Tooltip title="Card Border" placement="top">
						<Box sx={{ display: "flex", alignItems: "center" }}>
							<ThemeColorPicker
								color={toColor("hex", child?.props?.style?.borderColor || "transparent")}
								setColor={(c) => {}}
								onCommit={(c) => {
									const props = { ...(child.props || {}) };
									const style = { ...(props.style || {}) };
									style.borderColor = c.hex;
									if (c.hex && c.hex !== "transparent") {
										style.borderWidth = "1px";
										style.borderStyle = "solid";
									} else {
										style.borderWidth = "0px";
										style.borderStyle = "none";
									}
									props.style = style;
									child.props = props;
									update();
								}}
							/>
						</Box>
					</Tooltip>
					<Sep />

					{isGenericCard && (
						<>
							{/* Icon Pick */}
							<IconCollection
								onPickIcon={(icon) => {
									const content = { ...(child.content || {}) };
									content.icon = icon;
									child.content = content;
									update();
								}}
								selectedIcon={child?.content?.icon || "vector-selection"}
							>
								<Tooltip title="Change Icon" placement="top">
									<Box
										component="span"
										className={`mdi mdi-${child?.content?.icon || "vector-selection"}`}
										sx={{ fontSize: 20, cursor: "pointer" }}
									/>
								</Tooltip>
							</IconCollection>
							<Tooltip title="Icon color" placement="top">
								<Box sx={{ display: "flex", alignItems: "center" }}>
									<ThemeColorPicker
										color={toColor("hex", child?.props?.style?.iconColor || "#000000")}
										setColor={(c) => {}}
										onCommit={(c) => setNested("style", "iconColor", c.hex)}
									/>
								</Box>
							</Tooltip>
							<Select
								size="small"
								value={child?.props?.style?.iconSize || "medium"}
								onChange={(e) => setNested("style", "iconSize", e.target.value)}
								variant="standard"
								disableUnderline
								sx={{ minWidth: 45, fontSize: 12 }}
							>
								{ICON_SIZES.map((s) => (
									<MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
								))}
							</Select>
							<Sep />
						</>
					)}

					{ib("Duplicate", ContentCopyRounded, handleDuplicate)}
					{ib("Delete", DeleteRounded, handleDelete)}
				</>
			)}

			{isContent && (
				<>
					<IconCollection
						onPickIcon={(icon) => { if (!child.content) child.content = {}; child.content.icon = icon; update(); }}
						selectedIcon={child?.content?.icon || "star"}
					>
						<Tooltip title="Icon" placement="top">
							<Box
								component="span"
								className={`mdi mdi-${child?.content?.icon || "star"}`}
								sx={{ fontSize: 20, lineHeight: 1.2 }}
							/>
						</Tooltip>
					</IconCollection>
					<Tooltip title="Icon colour" placement="top">
						<Box>
							<ThemeColorPicker
								color={accentColor}
								setColor={(c) => { setAccentColor(c); }}
								onCommit={(c) => { setNested("style4", "color", c.hex); }}
							/>
						</Box>
					</Tooltip>
					<Sep />
					<IconButton
						size="small"
						onClick={() => setNested("style", "fontWeight", child?.props?.style?.fontWeight === 700 ? 400 : 700)}
						color={child?.props?.style?.fontWeight === 700 ? "primary" : "default"}
					>
						<FormatBoldRounded fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						onClick={() => setNested("style", "fontStyle", child?.props?.style?.fontStyle === "italic" ? "normal" : "italic")}
						color={child?.props?.style?.fontStyle === "italic" ? "primary" : "default"}
					>
						<FormatItalicRounded fontSize="small" />
					</IconButton>

					<IconButton
						size="small"
						onClick={() => setNested("style2", "fontWeight", child?.props?.style2?.fontWeight === 700 ? 400 : 700)}
						color={child?.props?.style2?.fontWeight === 700 ? "primary" : "default"}
					>
						<FormatBoldRounded fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						onClick={() => setNested("style2", "fontStyle", child?.props?.style2?.fontStyle === "italic" ? "normal" : "italic")}
						color={child?.props?.style2?.fontStyle === "italic" ? "primary" : "default"}
					>
						<FormatItalicRounded fontSize="small" />
					</IconButton>
					<Sep />
					{renderLinkInput()}
					<Sep />
					{ib("Duplicate", ContentCopyRounded, handleDuplicate)}
					{ib("Delete", DeleteRounded, handleDelete)}
				</>
			)}

			{isContentWithImage && (
				<>
					<ImageGallery
						onAddImage={(img) => { if (!child.content) child.content = {}; child.content.src = img; update(); }}
					>
						<Tooltip title="Image" placement="top">
							<ImageRounded sx={{ fontSize: 20 }} />
						</Tooltip>
					</ImageGallery>
					<Sep />
					<IconCollection
						onPickIcon={(icon) => { if (!child.content) child.content = {}; child.content.icon = icon; update(); }}
						selectedIcon={child?.content?.icon || "star"}
					>
						<Tooltip title="Icon" placement="top">
							<Box
								component="span"
								className={`mdi mdi-${child?.content?.icon || "star"}`}
								sx={{ fontSize: 20, lineHeight: 1.2 }}
							/>
						</Tooltip>
					</IconCollection>
					<Tooltip title="Icon colour" placement="top">
						<Box>
							<ThemeColorPicker
								color={accentColor}
								setColor={(c) => { setAccentColor(c); }}
								onCommit={(c) => { setNested("style4", "color", c.hex); }}
							/>
						</Box>
					</Tooltip>
					<Sep />
					<IconButton
						size="small"
						onClick={() => setNested("style", "fontWeight", child?.props?.style?.fontWeight === 700 ? 500 : 700)}
						color={child?.props?.style?.fontWeight === 700 ? "primary" : "default"}
					>
						<FormatBoldRounded fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						onClick={() => setNested("style", "fontStyle", child?.props?.style?.fontStyle === "italic" ? "normal" : "italic")}
						color={child?.props?.style?.fontStyle === "italic" ? "primary" : "default"}
					>
						<FormatItalicRounded fontSize="small" />
					</IconButton>

					<IconButton
						size="small"
						onClick={() => setNested("style2", "fontWeight", child?.props?.style2?.fontWeight === 700 ? 500 : 700)}
						color={child?.props?.style2?.fontWeight === 700 ? "primary" : "default"}
					>
						<FormatBoldRounded fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						onClick={() => setNested("style2", "fontStyle", child?.props?.style2?.fontStyle === "italic" ? "normal" : "italic")}
						color={child?.props?.style2?.fontStyle === "italic" ? "primary" : "default"}
					>
						<FormatItalicRounded fontSize="small" />
					</IconButton>
					<Sep />
					{renderLinkInput()}
					<Sep />
					{ib("Duplicate", ContentCopyRounded, handleDuplicate)}
					{ib("Delete", DeleteRounded, handleDelete)}
				</>
			)}

			{(child?.type === customComponentTypes.content3) && (
				<>
					<ImageGallery
						onAddImage={(img) => { if (!child.content) child.content = {}; child.content.src = img; update(); }}
					>
						<Tooltip title="Image" placement="top">
							<ImageRounded sx={{ fontSize: 20 }} />
						</Tooltip>
					</ImageGallery>
					<Sep />
					{renderLinkInput()}
					<Sep />
					{ib("Duplicate", ContentCopyRounded, handleDuplicate)}
					{ib("Delete", DeleteRounded, handleDelete)}
				</>
			)}

			{isTypo1 && (
				<>
					<Tooltip title="Text colour" placement="top">
						<Box>
							<ThemeColorPicker
								color={toColor("hex", child?.props?.style5?.textcolor || "#000000")}
								setColor={(c) => {}}
								onCommit={(c) => setNested("style5", "textcolor", c.hex)}
							/>
						</Box>
					</Tooltip>
					<Sep />
					<IconButton
						size="small"
						onClick={() => setNested("style1", "fontWeight", child?.props?.style1?.fontWeight === 700 ? 500 : 700)}
						color={child?.props?.style1?.fontWeight === 700 ? "primary" : "default"}
					>
						<FormatBoldRounded fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						onClick={() => setNested("style1", "fontStyle", child?.props?.style1?.fontStyle === "italic" ? "normal" : "italic")}
						color={child?.props?.style1?.fontStyle === "italic" ? "primary" : "default"}
					>
						<FormatItalicRounded fontSize="small" />
					</IconButton>
					<Select
						size="small"
						value={child?.props?.style1?.fontSize ?? ""}
						onChange={(e) => {
							if (!child.props) child.props = {};
							if (!child.props.style1) child.props.style1 = {};
							child.props.style1.fontSize = e.target.value;
							update();
						}}
						variant="standard" disableUnderline displayEmpty
						sx={{ minWidth: 50, fontSize: 13 }}
					>
						<MenuItem value=""><em>Size</em></MenuItem>
						{FONT_SIZES.map((s) => (
							<MenuItem key={s} value={s}>{s}</MenuItem>
						))}
					</Select>
					<Sep />
					{renderLinkInput()}
					<Sep />
					{ib("Duplicate", ContentCopyRounded, handleDuplicate)}
					{ib("Delete", DeleteRounded, handleDelete)}
				</>
			)}

			{(isPopover || isDropdown) && (
				<>
					{ib("Edit Items", ModeEditRounded, () => {
						child.showEditor = true;
						update();
					})}
					<Sep />
					{ib("Duplicate", ContentCopyRounded, handleDuplicate)}
					{ib("Delete", DeleteRounded, handleDelete)}
				</>
			)}

			{isForm && (
				<>
					{ib("Edit Form", ModeEditRounded, () => {
						child.showEditor = true;
						update();
					})}
					<Sep />
					{ib("Duplicate", ContentCopyRounded, handleDuplicate)}
					{ib("Delete", DeleteRounded, handleDelete)}
				</>
			)}
		</Stack >
		</Box>
	);
}
